import { hasValue } from "./common";

/**
 * Compare two string ignore case, accents etc
 * @param a
 * @param b
 * @param sensitivity default 'accent'
 * * "accent": Only strings that differ in base letters or accents and other diacritic marks compare as unequal. Examples: a ≠ b, a ≠ á, a = A.
 * * "base": Only strings that differ in base letters compare as unequal. Examples: a ≠ b, a = á, a = A.
 * * "case": Only strings that differ in base letters or case compare as unequal. Examples: a ≠ b, a = á, a ≠ A.
 * * "variant": Strings that differ in base letters, accents and other diacritic marks, or case compare as unequal. Other differences may also be taken into consideration. Examples: a ≠ b, a ≠ á, a ≠ A.
 * @returns boolean
 */
export const stringEquals = (a: string, b: string, sensitivity: 'accent' | 'base' = 'accent'): boolean => {
	a = a?.trim();
	b = b?.trim();
	if (!hasValue(a) || !hasValue(b)) return false;
	return a.localeCompare(b, undefined, { sensitivity }) === 0 || a === b;
}

/**
 * Compare two string ignore case
 * @param a
 * @param b
 * @returns boolean
 */
export const stringContains = (a?: string, b?: string): boolean => {
	a = a?.trim()?.toLocaleLowerCase();
	b = b?.trim()?.toLocaleLowerCase();
	if (!a || !hasValue(a) || !b || !hasValue(b)) return false;

	return a.includes(b) || b.includes(a);
}

/**
 *
 * @param str
 * @returns
 */
export const isCamelCase = (str: string): boolean => /^[a-z][A-Za-z]*$/.test(str); //camelCase

/**
 *
 * @param str
 * @returns
 */
export const isPascalCase = (str: string): boolean => /^[A-Z][A-Za-z]*$/.test(str); //PascalCase

/**
 *
 * @param str
 * @returns
 */
export const isUpperCase = (str: string): boolean => /^[A-Z]+$/.test(str.replace(/[^a-zA-Z]/gi, '')); //UPPERCASE | UPPER_CASE | UPPER CASE

/**
 *
 * @param value
 * @param extraReplace
 * @returns
 */
export const titleCase = (value: string, extraReplace?: RegExp): string | undefined => {
	if (!value || !hasValue(value)) return;

	if (extraReplace && hasValue(extraReplace)) value = value.replace(extraReplace, '');

	value = value
		.replace(/^(V_|I_|D_|T_|N_)/, '') //PREFIX XpressReadOnly
		.replace(/(_CODE|_ID)$/, '') //SUFFIX XpressReadOnly
		.replace(/(_MCD|_AIFLAG)$/, '') //SUFFIX XpressReadOnly (status, mcd)
		.replace(/(_ID|_id|_Id)$/i, '') //SUFFIX _ID
		.replace(/_/gi, ' ')
		.replace(/\s+/gi, ' ')
		.trim(); //.replace(/[A-Z]/g, ' $1');

	if (value.toLowerCase() === 'id') return value.toUpperCase();

	if (!isUpperCase(value) && (isCamelCase(value) || isPascalCase(value))) value = value.replace(/[A-Z]/g, (word: string) => ' ' + word).trim();
	return value.replace(
		/\w\S*/g,
		function (word: string) {
			return (word.charAt(0).toUpperCase() + word.substr(1).toLowerCase())
				.split('-')
				.map(val => val.charAt(0).toUpperCase() + val.substr(1))
				.join('-');
		}
	)
		.replace(/([A-Z])\s(?=[A-Z])/g, '$1') //keep upper letters together
		.trim()
}


/**
 * Return Acronym of string
 *
 * eg: 'Java Script Object Navigation' => JSON
 *
 * eg: 'john doe' => JD
 * @param str
 * @returns
 */
export const acronym = (str: string): string => {
	if (!hasValue(str)) return str;

	return str?.trim()?.split(/\s/)?.reduce((response, word) => response += word.slice(0, 1)?.toLocaleUpperCase(), '');
}
